// Streaming client for POST /api/wealth-chat/stream.
//
// axios cannot expose a progressive ReadableStream in the browser, so this
// module uses the native fetch API while reusing the SAME auth contract as
// the shared apiClient: Bearer JWT from auth-storage, httpOnly cookies via
// credentials:'include', and a single silent /auth/refresh retry on 401.
//
// NOTE: the chat router is mounted at /api/wealth-chat (NOT under /api/v1),
// so the host is derived from apiClient's baseURL with the /api/v1 suffix
// stripped. In dev this yields a same-origin path served by the Vite proxy.

import { apiClient } from '../../../api/client';
import { refreshAccessToken } from '../../../api/auth';
import { getToken, setToken } from '../../../api/auth-storage';

const CHAT_STREAM_PATH = '/api/wealth-chat/stream';

export class ChatAuthError extends Error {
    constructor() {
        super('Your session has expired. Please sign in again.');
        this.name = 'ChatAuthError';
    }
}

function resolveStreamUrl(): string {
    const base = (apiClient.defaults.baseURL ?? '').replace(/\/api\/v1\/?$/, '');
    return `${base}${CHAT_STREAM_PATH}`;
}

async function postStream(message: string, sessionId: string, signal?: AbortSignal): Promise<Response> {
    const token = getToken();
    return fetch(resolveStreamUrl(), {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'text/plain',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ message, session_id: sessionId }),
        signal,
    });
}

export interface StreamChatOptions {
    sessionId: string;
    signal?: AbortSignal;
    onChunk: (text: string) => void;
}

export async function streamWealthChat(
    message: string,
    { sessionId, signal, onChunk }: StreamChatOptions,
): Promise<void> {
    let response = await postStream(message, sessionId, signal);

    if (response.status === 401) {
        try {
            const { access_token } = await refreshAccessToken();
            setToken(access_token);
        } catch {
            throw new ChatAuthError();
        }
        response = await postStream(message, sessionId, signal);
        if (response.status === 401) throw new ChatAuthError();
    }

    if (!response.ok || !response.body) {
        throw new Error(`Chat request failed (HTTP ${response.status}).`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    try {
        while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            if (value) {
                const text = decoder.decode(value, { stream: true });
                if (text) onChunk(text);
            }
        }
        const tail = decoder.decode();
        if (tail) onChunk(tail);
    } finally {
        reader.releaseLock();
    }
}
